import { cva, type VariantProps } from 'class-variance-authority';

import type { PopoverCollapseBelow } from './popover.variants';

/**
 * PopoverButton over the Button `ghost` look at size `lg`, which supplies its
 * padding, label size and closed radius; icon-only it takes size `icon`, the
 * round icon button's size, padding and radius. Open is the `aria-expanded`
 * the popover sets, so the open fill needs no extra state. The open fill and
 * radius beat hover and press, and an open trigger does not underline.
 * The height is fixed (`h-fit` with a label, the icon button height without)
 * so a stretching flex row cannot grow the trigger and an attached panel
 * meets its bottom edge. An attached, labelled trigger in the Navbar bar is
 * pinned to the top of the bar (`self-start`, see
 * `popoverButtonBarTopClass`) instead of centered, so the open tab is flush
 * with the bar top and the panel hangs off the tab's bottom edge, over the
 * lower part of the bar. It keeps its own height: stretching it to the bar
 * height pushed the panel down to the bar's bottom edge. `labelHiddenBelow`
 * pins only from the breakpoint where its label shows, so it does not touch
 * the icon-only layout below it; icon-only (`labelHidden`) stays centered.
 * The open fill
 * fades on the panel's enter and exit timings, so trigger and panel read as
 * one surface. Under reduced motion nothing fades, whatever Button transition
 * a theme sets. `labelHiddenBelow` keeps the labelled `lg` trigger from that
 * breakpoint up and restates the icon-only size, padding, radius and icon
 * size below it. Those classes come after the `aria-expanded:` ones in the
 * stylesheet, so the open radius and attached corners are restated below it
 * too.
 */
export const popoverButtonVariants = cva(
  [
    'font-medium',
    'motion-safe:transition-popover-button',
    'motion-safe:duration-popover-close motion-safe:ease-popover-close',
    'motion-safe:aria-expanded:duration-popover-open motion-safe:aria-expanded:ease-popover-open',
    'motion-reduce:transition-none motion-reduce:duration-[0s]',
    'hover:bg-popover-button-hover active:bg-popover-button-hover',
    'aria-expanded:bg-popover-button-open',
    'aria-expanded:hover:bg-popover-button-open aria-expanded:active:bg-popover-button-open',
    'aria-expanded:hover:no-underline',
    'aria-expanded:rounded-popover-button-open',
  ],
  {
    variants: {
      attached: {
        true: 'aria-expanded:rounded-b-none',
        false: '',
      },
      iconOnly: {
        true: '',
        false: '',
      },
      labelHiddenBelow: {
        md: [
          'max-md:h-button-icon-only max-md:w-button-icon-only max-md:p-button-icon-only',
          'max-md:rounded-button-icon-only max-md:gap-0 max-md:text-sm',
          'max-md:[&_svg]:size-button-icon-only-icon max-md:[&_[data-hen-icon]]:size-button-icon-only-icon',
          'max-md:aria-expanded:rounded-popover-button-open',
        ],
        lg: [
          'max-lg:h-button-icon-only max-lg:w-button-icon-only max-lg:p-button-icon-only',
          'max-lg:rounded-button-icon-only max-lg:gap-0 max-lg:text-sm',
          'max-lg:[&_svg]:size-button-icon-only-icon max-lg:[&_[data-hen-icon]]:size-button-icon-only-icon',
          'max-lg:aria-expanded:rounded-popover-button-open',
        ],
      },
    },
    compoundVariants: [
      {
        iconOnly: false,
        class: 'h-fit',
      },
      {
        attached: true,
        labelHiddenBelow: 'md',
        class: 'max-md:aria-expanded:rounded-b-none',
      },
      {
        attached: true,
        labelHiddenBelow: 'lg',
        class: 'max-lg:aria-expanded:rounded-b-none',
      },
    ],
    defaultVariants: {
      attached: false,
      iconOnly: false,
    },
  },
);

/**
 * Pins an attached labelled trigger to the top of the Navbar actions row so
 * its open tab is flush with the bar top. When the label is hidden below a
 * breakpoint, pins only from that breakpoint up.
 */
export function popoverButtonBarTopClass(
  pinsToBarTop: boolean,
  labelHiddenBelow?: PopoverCollapseBelow,
): string | undefined {
  if (!pinsToBarTop) return undefined;
  if (labelHiddenBelow === 'md') return 'md:self-start';
  if (labelHiddenBelow === 'lg') return 'lg:self-start';
  return 'self-start';
}

/** Closed icon: hidden once an open icon is set and the trigger is open. */
export const POPOVER_BUTTON_ICON_CLOSED_CLASS =
  '[[aria-expanded=true]>&]:hidden';

/** The label below `labelHiddenBelow`: visually hidden, still the accessible name. */
export const POPOVER_BUTTON_LABEL_HIDDEN_BELOW_CLASS = {
  md: 'max-md:sr-only',
  lg: 'max-lg:sr-only',
} as const;

/** Open icon: shown only while the trigger is open. */
export const POPOVER_BUTTON_ICON_OPEN_CLASS =
  'hidden [[aria-expanded=true]>&]:inline-block';

export type PopoverButtonVariantProps = VariantProps<
  typeof popoverButtonVariants
>;
